/**
 * Bagagem — regras puras.
 */

export type BaggageScope = 'private' | 'family' | 'trip';

export const BAGGAGE_CATEGORIES = [
  'roupas',
  'documentos',
  'eletronicos',
  'higiene',
  'medicamentos',
  'criancas',
  'praia',
  'outros',
] as const;
export type BaggageCategory = (typeof BAGGAGE_CATEGORIES)[number];

export const BAGGAGE_CATEGORY_LABELS: Record<BaggageCategory, string> = {
  roupas: 'Roupas',
  documentos: 'Documentos',
  eletronicos: 'Eletrônicos',
  higiene: 'Higiene',
  medicamentos: 'Medicamentos',
  criancas: 'Crianças',
  praia: 'Praia / piscina',
  outros: 'Outros',
};

export function isBaggageCategory(v: unknown): v is BaggageCategory {
  return typeof v === 'string' && (BAGGAGE_CATEGORIES as readonly string[]).includes(v);
}

export interface BaggageItemLike {
  id: string;
  title: string;
  category: BaggageCategory;
  packedAt: string | null;
  assigneeMemberId?: string | null;
  sortOrder: number;
}

/** "18 de 27 na mala". */
export function baggageProgress(itens: readonly Pick<BaggageItemLike, 'packedAt'>[]): { packed: number; total: number; done: boolean } {
  const total = itens.length;
  const packed = itens.filter((i) => i.packedAt !== null).length;
  return { packed, total, done: total > 0 && packed === total };
}

/**
 * Agrupa por categoria, na ordem fixa, só as que têm itens. Dentro da
 * categoria, pela ordem de inserção — marcado NÃO salta para o fim: a
 * pessoa perde a referência visual.
 */
export function groupBaggage<T extends BaggageItemLike>(itens: readonly T[]): { category: BaggageCategory; itens: T[]; packed: number }[] {
  const grupos: { category: BaggageCategory; itens: T[]; packed: number }[] = [];
  for (const c of BAGGAGE_CATEGORIES) {
    const doGrupo = itens.filter((i) => i.category === c).sort((a, b) => a.sortOrder - b.sortOrder);
    if (doGrupo.length === 0) continue;
    grupos.push({ category: c, itens: doGrupo, packed: doGrupo.filter((i) => i.packedAt !== null).length });
  }
  return grupos;
}

/**
 * Texto colado vira itens: uma linha por item. Vírgula NÃO separa (nomes
 * de item têm vírgula). Marcadores de lista comuns são tirados; vazios
 * e repetidos (ignorando caixa) somem. Teto: 100 de uma vez.
 */
export function splitPastedItems(texto: string): string[] {
  const vistos = new Set<string>();
  const saida: string[] = [];
  for (const linha of texto.split(/\r?\n/)) {
    const limpo = linha
      .replace(/^\s*(?:[-*•▪◦]|\d+[.)])\s*/, '')
      .replace(/^\s*\[[ xX]\]\s*/, '')
      .trim();
    if (!limpo) continue;
    const chave = limpo.toLocaleLowerCase();
    if (vistos.has(chave)) continue;
    vistos.add(chave);
    saida.push(limpo.slice(0, 120));
    if (saida.length >= 100) break;
  }
  return saida;
}

/** Quem pode editar/excluir um item de viagem sem permissão nova. */
export function canEditBaggageItem(params: {
  scope: BaggageScope;
  createdByMemberId: string;
  assigneeMemberId: string | null;
  meuMemberId: string;
  meuPapel: 'owner' | 'admin' | 'collaborator' | 'participant';
  minhaFamiliaId: string | null;
  familyId: string | null;
}): boolean {
  if (params.scope === 'private') return params.createdByMemberId === params.meuMemberId;
  if (params.scope === 'family') return params.familyId !== null && params.familyId === params.minhaFamiliaId;
  return (
    params.createdByMemberId === params.meuMemberId ||
    params.assigneeMemberId === params.meuMemberId ||
    params.meuPapel === 'owner' ||
    params.meuPapel === 'admin'
  );
}
